/**
 * Gruppi ordinati di credenziali: si prova la prima, se fallisce la seconda.
 *
 * ## Perché l'ordine è visibile e modificabile, e non implicito
 *
 * La sonda si ferma alla prima credenziale che risponde. La sequenza quindi non è
 * una preferenza estetica: decide **quanto dura la scansione** — ogni tentativo
 * fallito costa un timeout SNMP per ogni apparato dell'ambito — e **su quali
 * apparati si accumulano autenticazioni fallite**, che con SNMPv3 possono
 * bloccare l'utenza. Un ordine deciso dall'inserimento nasconderebbe una scelta
 * con queste conseguenze, e chi la subisce non avrebbe modo di vederla.
 *
 * Per questo il riordino è qui, con le frecce accanto a ogni riga e il numero
 * d'ordine davanti: non serve capire come sia stato composto il gruppo per
 * sapere cosa verrà provato per primo.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import type { Credential, CredentialGroup } from '../api/types'
import { Card, EmptyState, ErrorBanner, Spinner } from './common'
import { t } from '../i18n/lingua'

/** Oltre questo numero il costo della cascata supera l'utilità (vedi il servizio). */
const MAX_MEMBERS = 10

export default function CredentialGroups({
  credentials,
  groups,
  loading,
  onChanged,
}: {
  credentials: Credential[]
  groups: CredentialGroup[]
  loading: boolean
  onChanged: () => void
}) {
  const [name, setName] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const byId = new Map(credentials.map((c) => [c.id, c]))
  // Le credenziali non ancora nel gruppo in composizione: riproporre una già
  // scelta produrrebbe un doppione, che l'API rifiuta — e il rifiuto arriverebbe
  // solo al salvataggio, dopo aver ricomposto tutto l'elenco.
  const addable = credentials.filter((c) => !picked.includes(c.id))

  async function create(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.createCredentialGroup(name.trim(), picked)
      setName('')
      setPicked([])
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  /** Riordino e rimozione passano entrambi da qui: l'API sostituisce l'elenco. */
  async function save(group: CredentialGroup, credentialIds: string[]) {
    setError(null)
    try {
      await api.updateCredentialGroup(group.id, group.name, credentialIds, group.description ?? undefined)
      onChanged()
    } catch (err) {
      setError(err)
    }
  }

  async function remove(group: CredentialGroup) {
    if (!window.confirm(t('Eliminare il gruppo di credenziali «{nome}»? Le credenziali restano; i job che usano il gruppo non lo troveranno più. Non si può annullare.', { nome: group.name }))) return
    setError(null)
    try {
      await api.deleteCredentialGroup(group.id)
      onChanged()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <>
      <ErrorBanner error={error} />

      <Card title={t('Nuovo gruppo di credenziali')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('La sonda prova le credenziali in ordine e si ferma alla prima che risponde. Serve quando gli apparati non hanno tutti la stessa community o lo stesso utente — tipicamente dopo una migrazione, o fra sedi acquisite in momenti diversi.')}
        </p>
        <form onSubmit={create}>
          <div className="row">
            <div className="field">
              <label htmlFor="cg-name">{t('Nome del gruppo')}</label>
              <input
                id="cg-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('SNMP standard')}
                required
              />
              <p className="field__hint">
                {t('Verrà scelto in fase di discovery: un nome che dica dove o quando si applica («Filiali», «Apparati pre-2020») si riconosce fra sei mesi.')}
              </p>
            </div>
            <div className="field">
              <label htmlFor="cg-add">{t('Aggiungi credenziale')}</label>
              <select
                id="cg-add"
                value=""
                disabled={picked.length >= MAX_MEMBERS}
                onChange={(e) => {
                  if (e.target.value) setPicked([...picked, e.target.value])
                }}
              >
                <option value="">
                  {picked.length >= MAX_MEMBERS ? t('Massimo {n}', { n: MAX_MEMBERS }) : t('Seleziona…')}
                </option>
                {addable.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.kind})
                  </option>
                ))}
              </select>
              <p className="field__hint">
                {t('Ogni credenziale sbagliata prima di quella giusta costa un timeout per ogni apparato dell’ambito: metti per prima quella che copre più apparati.')}
              </p>
            </div>
          </div>

          {picked.length > 0 && (
            <Sequence
              ids={picked}
              byId={byId}
              onChange={setPicked}
              onRemove={(id) => setPicked(picked.filter((p) => p !== id))}
            />
          )}

          <button type="submit" disabled={busy || picked.length === 0}>
            {busy ? t('Salvataggio…') : t('Crea gruppo')}
          </button>
          {picked.length === 0 && (
            <p className="field__hint">
              {t('Un gruppo vuoto non servirebbe a niente: una discovery che lo usasse non avrebbe nulla da provare, e ogni apparato risulterebbe irraggiungibile — che si legge come un problema di rete.')}
            </p>
          )}
        </form>
      </Card>

      <Card title={t('Gruppi configurati')}>
        {loading && groups.length === 0 ? (
          <Spinner />
        ) : groups.length === 0 ? (
          <EmptyState
            title={t('Nessun gruppo')}
            hint={t('Con una sola credenziale per tipo non serve: creane uno quando gli apparati non rispondono tutti alla stessa.')}
          />
        ) : (
          groups.map((g) => (
            <div key={g.id} className="kindform" style={{ marginBottom: 12 }}>
              <div className="toolbar" style={{ justifyContent: 'space-between' }}>
                <h3 className="kindform__title" style={{ margin: 0 }}>
                  {g.name}{' '}
                  <span className="text-muted" style={{ fontWeight: 400 }}>
                    — {g.members.length === 1
                      ? t('{n} credenziale', { n: g.members.length })
                      : t('{n} credenziali', { n: g.members.length })}
                  </span>
                </h3>
                <button className="danger" onClick={() => remove(g)}>
                  {t('Elimina gruppo')}
                </button>
              </div>

              {g.members.length === 0 ? (
                <div className="banner banner--error">
                  {t('Questo gruppo non ha più credenziali: probabilmente sono state eliminate. Una discovery che lo usasse verrebbe rifiutata.')}
                </div>
              ) : (
                <Sequence
                  ids={g.members.map((m) => m.credential_id)}
                  byId={
                    new Map(
                      g.members.map((m) => [
                        m.credential_id,
                        // Il nome arriva dall'API insieme al gruppo: le credenziali
                        // dell'elenco a fianco potrebbero non contenerlo se un
                        // giorno quella lista venisse filtrata.
                        { id: m.credential_id, name: m.name ?? m.credential_id, kind: m.kind ?? '—' },
                      ]),
                    ) as Map<string, { id: string; name: string; kind: string }>
                  }
                  onChange={(ids) => save(g, ids)}
                  onRemove={(id) =>
                    save(
                      g,
                      g.members.map((m) => m.credential_id).filter((c) => c !== id),
                    )
                  }
                />
              )}
            </div>
          ))
        )}
      </Card>
    </>
  )
}

/**
 * L'elenco ordinato, con le frecce.
 *
 * Frecce e non trascinamento: il riordino si fa raramente e su pochi elementi, e
 * il drag and drop non si usa da telefono senza una libreria che intercetti il
 * gesto di scorrimento della pagina.
 */
function Sequence({
  ids,
  byId,
  onChange,
  onRemove,
}: {
  ids: string[]
  byId: Map<string, { id: string; name: string; kind: string }>
  onChange: (ids: string[]) => void
  onRemove: (id: string) => void
}) {
  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    const next = [...ids]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>{t('Credenziale')}</th>
            <th>{t('Sistema')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ids.map((id, i) => {
            const c = byId.get(id)
            return (
              <tr key={id}>
                <td>{i + 1}</td>
                <td>
                  {c?.name ?? id}
                  {i === 0 && (
                    <span className="chip" title={t('È quella che verrà provata su ogni apparato')}>
                      {t('provata per prima')}
                    </span>
                  )}
                </td>
                <td>{c?.kind ?? '—'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    className="secondary"
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label={t('Sposta {nome} più in alto', { nome: c?.name ?? id })}
                    title={t('Provala prima')}
                  >
                    ↑
                  </button>{' '}
                  <button
                    className="secondary"
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === ids.length - 1}
                    aria-label={t('Sposta {nome} più in basso', { nome: c?.name ?? id })}
                    title={t('Provala dopo')}
                  >
                    ↓
                  </button>{' '}
                  <button
                    className="danger"
                    type="button"
                    onClick={() => onRemove(id)}
                    aria-label={t('Togli {nome} dal gruppo', { nome: c?.name ?? id })}
                  >
                    {t('Togli')}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
